import React, { useContext, useEffect, useRef, useState } from 'react';
import { Edit3, MessageSquare, Send, Trash2, X } from 'lucide-react';
import { AuthContext } from '../../hooks/useAuth';
import { useComments } from '../../hooks/useComments';
import { userService } from '../../services/api/users';
import { CommentEntry } from '../../types/comments';
import { User } from '../../types/mold';
import UserProfileImage from '../KanbanBoard/UserProfileImage';
import {
  buildMentionSegments,
  extractMentions,
  findMentionQuery,
  getMentionSuggestions,
  insertMention,
  MentionQuery,
} from './commentUtils';

interface CommentsPanelProps {
  moldOrderId: number;
}

interface CommentComposerProps {
  users: User[];
  initialValue?: string;
  submitting: boolean;
  submitLabel: string;
  onCancel?: () => void;
  onSubmit: (body: string) => Promise<void>;
}

const MAX_COMMENT_LENGTH = 2000;

const formatCommentTime = (iso: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat('th-TH', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const getDisplayName = (user?: { fullname?: string; username?: string } | null) =>
  user?.fullname || user?.username || 'ไม่ระบุ';

const getOptionalDisplayName = (user?: { fullname?: string; username?: string } | null) =>
  user?.fullname || user?.username || '';

const getMentionDisplayText = (
  mention: { fullname?: string; username?: string } | undefined,
  fallbackText: string,
) => {
  const displayName = getOptionalDisplayName(mention);
  return displayName ? `@${displayName}` : fallbackText;
};

const isEditedComment = (comment: CommentEntry) =>
  comment.updatedAt !== comment.createdAt && !comment.isDeleted;

const getTextareaCaretCoordinates = (
  textarea: HTMLTextAreaElement,
  caretIndex: number,
) => {
  const computed = window.getComputedStyle(textarea);
  const mirror = document.createElement('div');
  const span = document.createElement('span');
  const styleProperties = [
    'borderBottomWidth',
    'borderLeftWidth',
    'borderRightWidth',
    'borderTopWidth',
    'boxSizing',
    'fontFamily',
    'fontSize',
    'fontStyle',
    'fontWeight',
    'letterSpacing',
    'lineHeight',
    'paddingBottom',
    'paddingLeft',
    'paddingRight',
    'paddingTop',
    'textTransform',
    'whiteSpace',
    'wordBreak',
    'wordSpacing',
  ] as const;

  styleProperties.forEach((property) => {
    mirror.style[property] = computed[property];
  });

  mirror.style.position = 'absolute';
  mirror.style.visibility = 'hidden';
  mirror.style.overflow = 'hidden';
  mirror.style.top = '0';
  mirror.style.left = '-9999px';
  mirror.style.width = `${textarea.offsetWidth}px`;
  mirror.style.height = 'auto';
  mirror.style.whiteSpace = 'pre-wrap';
  mirror.style.wordBreak = 'break-word';
  mirror.textContent = textarea.value.slice(0, caretIndex);
  span.textContent = textarea.value.slice(caretIndex) || '.';
  mirror.appendChild(span);
  document.body.appendChild(mirror);

  const lineHeight = Number.parseFloat(computed.lineHeight) ||
    Number.parseFloat(computed.fontSize) * 1.2 ||
    20;
  const coordinates = {
    left: span.offsetLeft - textarea.scrollLeft,
    top: span.offsetTop - textarea.scrollTop,
    lineHeight,
  };

  document.body.removeChild(mirror);
  return coordinates;
};

const CommentText: React.FC<{ comment: CommentEntry }> = ({ comment }) => {
  const segments = buildMentionSegments(comment.body, comment.mentions || []);

  return (
    <p className="whitespace-pre-wrap break-words text-sm leading-6 text-gray-800">
      {segments.map((segment, index) =>
        segment.mention ? (
          <span
            key={`${segment.text}-${index}`}
            className="rounded-full bg-blue-50 px-1.5 py-0.5 font-medium text-blue-700"
            title={getDisplayName(segment.mention)}
          >
            {getMentionDisplayText(segment.mention, segment.text)}
          </span>
        ) : (
          <React.Fragment key={`${segment.text}-${index}`}>{segment.text}</React.Fragment>
        ),
      )}
    </p>
  );
};

const MentionHighlightedText: React.FC<{
  body: string;
  mentions: ReturnType<typeof extractMentions>;
  variant?: 'display' | 'editor';
}> = ({ body, mentions, variant = 'display' }) => {
  const segments = buildMentionSegments(body, mentions);
  const mentionClassName = variant === 'editor'
    ? 'rounded bg-blue-100 text-blue-700'
    : 'rounded-full bg-blue-100 px-1.5 py-0.5 font-semibold text-blue-700';

  return (
    <p className="whitespace-pre-wrap break-words text-sm leading-6 text-gray-800">
      {segments.map((segment, index) =>
        segment.mention ? (
          <span
            key={`${segment.text}-${index}`}
            className={mentionClassName}
            title={getDisplayName(segment.mention)}
          >
            {variant === 'editor'
              ? segment.text
              : getMentionDisplayText(segment.mention, segment.text)}
          </span>
        ) : (
          <React.Fragment key={`${segment.text}-${index}`}>{segment.text}</React.Fragment>
        ),
      )}
    </p>
  );
};

const CommentComposer: React.FC<CommentComposerProps> = ({
  users,
  initialValue = '',
  submitting,
  submitLabel,
  onCancel,
  onSubmit,
}) => {
  const composerRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const editorOverlayRef = useRef<HTMLDivElement | null>(null);
  const [body, setBody] = useState(initialValue);
  const [error, setError] = useState('');
  const [mentionQuery, setMentionQuery] = useState<MentionQuery | null>(null);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionPosition, setMentionPosition] = useState<{ top: number; left: number } | null>(null);

  const suggestions = mentionQuery
    ? getMentionSuggestions(users, mentionSearch)
    : [];
  const liveMentions = extractMentions(body, users);
  const mentionedUsers = Array.from(
    new Map(
      liveMentions.map((mention) => [mention.userId, mention]),
    ).values(),
  );

  const updateMentionQuery = (value: string, caretIndex: number, textarea: HTMLTextAreaElement | null = textareaRef.current) => {
    const nextMentionQuery = findMentionQuery(value, caretIndex);
    setMentionQuery(nextMentionQuery);
    setMentionSearch(nextMentionQuery?.query ?? '');

    if (!nextMentionQuery || !textarea || !composerRef.current) {
      setMentionPosition(null);
      return;
    }

    const caret = getTextareaCaretCoordinates(textarea, caretIndex);
    const popoverWidth = 288;
    const containerWidth = composerRef.current.clientWidth || textarea.offsetWidth;
    const nextLeft = Math.min(
      Math.max(textarea.offsetLeft + caret.left, 8),
      Math.max(8, containerWidth - popoverWidth - 8),
    );

    setMentionPosition({
      left: nextLeft,
      top: textarea.offsetTop + caret.top + caret.lineHeight + 8,
    });
  };

  const handleBodyChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextBody = event.target.value;
    setBody(nextBody);
    setError('');
    updateMentionQuery(nextBody, event.target.selectionStart, event.target);
  };

  const handleEditorScroll = (event: React.UIEvent<HTMLTextAreaElement>) => {
    if (editorOverlayRef.current) {
      editorOverlayRef.current.scrollTop = event.currentTarget.scrollTop;
      editorOverlayRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
    updateMentionQuery(event.currentTarget.value, event.currentTarget.selectionStart, event.currentTarget);
  };

  const handleSelectMention = (user: User) => {
    if (!mentionQuery) {
      return;
    }

    const next = insertMention(body, mentionQuery, user.username);
    setBody(next.value);
    setMentionQuery(null);
    setMentionSearch('');
    setMentionPosition(null);

    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(next.caretIndex, next.caretIndex);
    });
  };

  const handleSubmit = async () => {
    const trimmed = body.trim();
    if (!trimmed) {
      setError('กรุณาใส่ความคิดเห็น');
      return;
    }
    if (body.length > MAX_COMMENT_LENGTH) {
      setError(`ความคิดเห็นต้องไม่เกิน ${MAX_COMMENT_LENGTH} ตัวอักษร`);
      return;
    }

    await onSubmit(body);
    if (!initialValue) {
      setBody('');
    }
    setMentionQuery(null);
    setMentionSearch('');
    setMentionPosition(null);
  };

  return (
    <div ref={composerRef} className="relative">
      <div className="relative min-h-[104px] rounded-xl border border-gray-200 bg-white shadow-sm transition focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100">
        <div
          ref={editorOverlayRef}
          className="pointer-events-none absolute inset-0 overflow-hidden px-3 py-2.5 text-sm leading-6 text-gray-900"
          aria-hidden="true"
        >
          {body ? (
            <MentionHighlightedText body={body} mentions={liveMentions} variant="editor" />
          ) : (
            <span className="text-gray-400">เพิ่มความคิดเห็น... พิมพ์ @ เพื่อกล่าวถึงผู้ใช้</span>
          )}
        </div>
        <textarea
          ref={textareaRef}
          value={body}
          onChange={handleBodyChange}
          onKeyUp={(event) => updateMentionQuery(event.currentTarget.value, event.currentTarget.selectionStart, event.currentTarget)}
          onClick={(event) => updateMentionQuery(event.currentTarget.value, event.currentTarget.selectionStart, event.currentTarget)}
          onScroll={handleEditorScroll}
          maxLength={MAX_COMMENT_LENGTH + 1}
          className="relative z-10 min-h-[104px] w-full resize-y rounded-xl border-0 bg-transparent px-3 py-2.5 text-sm leading-6 text-transparent caret-gray-900 outline-none selection:bg-blue-200 placeholder:text-transparent"
          placeholder="เพิ่มความคิดเห็น... พิมพ์ @ เพื่อกล่าวถึงผู้ใช้"
        />
      </div>

      {mentionQuery && mentionPosition && (
        <div
          role="listbox"
          aria-label="ตัวเลือกผู้ถูกกล่าวถึง"
          className="absolute z-20 max-h-56 w-72 overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
          style={{ left: mentionPosition.left, top: mentionPosition.top }}
        >
          <div className="border-b border-gray-100 p-2">
            <input
              type="text"
              value={mentionSearch}
              onChange={(event) => setMentionSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  setMentionQuery(null);
                  setMentionSearch('');
                  setMentionPosition(null);
                  textareaRef.current?.focus();
                }
              }}
              className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm text-gray-900 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
              placeholder="ค้นหาผู้ใช้ที่จะกล่าวถึง"
            />
          </div>
          {suggestions.length > 0 ? (
            suggestions.map((user) => (
              <button
                key={user.id}
                type="button"
                role="option"
                aria-selected="false"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleSelectMention(user)}
                className="flex w-full flex-col px-3 py-2 text-left hover:bg-blue-50"
              >
                <span className="text-sm font-medium text-gray-900">{getDisplayName(user)}</span>
                <span className="text-xs text-gray-500">@{user.username}</span>
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500">
              ไม่พบผู้ใช้ที่ตรงกับ "{mentionSearch}"
            </div>
          )}
        </div>
      )}

      {mentionedUsers.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
          <span>กล่าวถึง:</span>
          {mentionedUsers.map((mention) => (
            <span
              key={mention.userId}
              className="rounded-full bg-blue-100 px-2 py-1 font-medium text-blue-700"
            >
              @{getDisplayName(mention)}
            </span>
          ))}
        </div>
      )}

      <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-xs text-gray-500">
          ใช้ @username เพื่อแจ้งเตือนผู้ใช้ที่เกี่ยวข้อง
          {error && <span className="ml-2 text-red-600">{error}</span>}
        </div>
        <div className="flex items-center gap-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
            >
              <X className="h-4 w-4" />
              ยกเลิก
            </button>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
          >
            <Send className="h-4 w-4" />
            {submitting ? 'กำลังบันทึก...' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const CommentsPanel: React.FC<CommentsPanelProps> = ({ moldOrderId }) => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const auth = useContext(AuthContext);
  const user = auth?.user ?? null;
  const {
    comments,
    loading,
    submitting,
    error,
    createComment,
    updateComment,
    deleteComment,
  } = useComments(moldOrderId, user?.user_id, Boolean(user?.user_id));
  const [users, setUsers] = useState<User[]>([]);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);

  useEffect(() => {
    if (!user?.user_id) {
      setUsers([]);
      return;
    }

    let isMounted = true;

    userService.getUsers(false)
      .then((response) => {
        if (isMounted) {
          setUsers(response.data as User[]);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch users for mentions:', err);
      });

    return () => {
      isMounted = false;
    };
  }, [user?.user_id]);

  useEffect(() => {
    if (window.location.hash !== '#comments') {
      return;
    }

    window.setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }, []);

  const canManageComment = (comment: CommentEntry) =>
    user?.user_id === comment.author?.id ||
    user?.role === 'ADMIN' ||
    user?.permission?.includes('ADMIN');

  const handleCreate = async (body: string) => {
    await createComment({
      body,
      mentions: extractMentions(body, users),
    });
  };

  const handleUpdate = async (commentId: number, body: string) => {
    await updateComment(commentId, {
      body,
      mentions: extractMentions(body, users),
    });
    setEditingCommentId(null);
  };

  const handleDelete = async (commentId: number) => {
    if (!window.confirm('ต้องการลบความคิดเห็นนี้หรือไม่?')) {
      return;
    }
    await deleteComment(commentId);
  };

  return (
    <section
      ref={sectionRef}
      id="comments"
      className="mt-4 scroll-mt-20 rounded-2xl border border-slate-200 bg-slate-50/70 p-3 sm:p-4"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="rounded-xl bg-blue-100 p-2 text-blue-700">
            <MessageSquare className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">ความคิดเห็น</h2>
            <p className="text-xs text-gray-500">พูดคุยเกี่ยวกับใบงานนี้และกล่าวถึงผู้เกี่ยวข้องได้</p>
          </div>
        </div>
        <span className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-gray-600 shadow-sm">
          {comments.length} รายการ
        </span>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
        <CommentComposer
          users={users}
          submitting={submitting}
          submitLabel="ส่งความคิดเห็น"
          onSubmit={handleCreate}
        />
      </div>

      {error && (
        <div className="mt-3 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mt-4 space-y-3">
        {loading ? (
          <div className="rounded-xl border border-dashed border-gray-200 bg-white px-4 py-6 text-center text-sm text-gray-500">
            กำลังโหลดความคิดเห็น...
          </div>
        ) : comments.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 bg-white px-4 py-6 text-center text-sm text-gray-500">
            ยังไม่มีความคิดเห็น
          </div>
        ) : (
          comments.map((comment) => (
            <article
              key={comment.id}
              className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm"
            >
              <div className="mb-2 flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2.5">
                  <UserProfileImage
                    currentAssignee={{
                      id: comment.author.id,
                      username: comment.author.username,
                      isActive: true,
                      roleId: 0,
                      createdAt: '',
                      updatedAt: '',
                      name: getDisplayName(comment.author),
                    }}
                    size="sm"
                  />
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      {getDisplayName(comment.author)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatCommentTime(comment.createdAt)}
                      {isEditedComment(comment) && (
                        <span>
                          {' · แก้ไขแล้ว'}
                          {getOptionalDisplayName(comment.lastEditedByUser) &&
                            ` โดย ${getOptionalDisplayName(comment.lastEditedByUser)}`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {!comment.isDeleted && canManageComment(comment) && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setEditingCommentId(comment.id)}
                      className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-blue-700"
                      title="แก้ไขความคิดเห็น"
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(comment.id)}
                      className="rounded-lg p-2 text-gray-500 hover:bg-red-50 hover:text-red-700"
                      title="ลบความคิดเห็น"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              {comment.isDeleted ? (
                <p className="rounded-xl bg-gray-50 px-3 py-2 text-sm italic text-gray-500">
                  ความคิดเห็นนี้ถูกลบแล้ว
                </p>
              ) : editingCommentId === comment.id ? (
                <CommentComposer
                  users={users}
                  initialValue={comment.body}
                  submitting={submitting}
                  submitLabel="บันทึกการแก้ไข"
                  onCancel={() => setEditingCommentId(null)}
                  onSubmit={(body) => handleUpdate(comment.id, body)}
                />
              ) : (
                <CommentText comment={comment} />
              )}
            </article>
          ))
        )}
      </div>
    </section>
  );
};

export default CommentsPanel;
